/**
 * UsersPage — Identity & Access · Users (v0.8.0 rebuild, Bill 2026-05-15).
 *
 * Implements docs/admin-panel/users-page-spec-v08.md §3.
 *
 * Two-column Edit modal:
 *   - Left: Entra-derived attrs (read-only; refresh via "Sync from Entra").
 *   - Right: admin-controlled (officer code, branch override, region,
 *     active, training, roles, subordinates).
 * Header: 96×96 photo + name + per-user Sync from Entra button.
 *
 * No more `OfficerTeam` or `IsCompliance` — both dropped in v0.8.0 schema.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, BoolField, TextArea } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';
import { Icon } from '@/components/Icon';
import { EntraUserPicker } from '@/components/EntraPicker';
import { UserToursSection } from './UserToursSection';
import type {
  EntraUserCandidate,
  SingleUserDiffResult,
  SubordinateOverrideRow,
  UserAccessAuditRow,
} from '@retail-ops/api-client';
import {
  type CatalogRole,
  auditActionLabel,
  canonicalCode,
  canonicalCodes,
  effectiveAccess,
  orderAdds,
  orderRemoves,
  roleGrants,
  roleLabel,
} from './roleCatalog';

/** Message text from an ApiError (thrown on non-2xx) or any other throwable. */
function errText(e: unknown): string {
  if (e && typeof e === 'object') {
    const m = (e as { message?: unknown }).message;
    if (typeof m === 'string' && m) return m;
  }
  return String(e);
}

/** What one Save will do — shown for confirmation before anything is written. */
interface SavePlan {
  adds: string[];
  removes: string[];
  fieldChanges: string[];
  categoriesChanged: boolean;
}

interface UserRow {
  userId: number;
  email: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  officerCode: string | null;
  branchCode: string | null;
  regionCode: string | null;
  entraObjectId: string | null;
  // Entra-derived (read-only display)
  department: string | null;
  jobTitle: string | null;
  officeLocation: string | null;
  mobilePhone: string | null;
  businessPhone: string | null;
  supervisorUserId: number | null;
  photoBase64: string | null;
  photoUpdatedAt: string | null;
  // Manual flags
  isActive: boolean;
  isOfficer: boolean;
  trainingMode: boolean;
  missingOfficerCode: boolean;
  // Cart-mode / sandbox (collapsible section)
  isInSandboxMode: boolean;
  /** Test mode (Bill 2026-10-07): live Core Director, test customers only. */
  isTestIdentitiesOnly: boolean;
  isUATEnrolled: boolean;
  hasTestLiveAccess: boolean;
  testRecipientEmail: string | null;
  testRecipientPhone: string | null;
  /** Core Director operator number — maintained by SysOps (Core Operator Numbers list); read-only here. */
  operatorNumber: string | null;
  /** Role codes in catalog spelling, de-duplicated case-insensitively (what the checkboxes edit). */
  roleCodes: ReadonlyArray<string>;
  /** Role codes exactly as stored (front-end gates in other apps compare these case-sensitively). */
  storedRoleCodes: ReadonlyArray<string>;
}

interface RoleAssignmentRaw { userId?: number; roleCode?: string }
interface UserRolesEnvelope {
  users?: ReadonlyArray<Record<string, unknown>>;
  roles?: ReadonlyArray<RoleAssignmentRaw>;
}

interface BranchOption { code: string; name: string }
interface RegionOption { code: string; name: string }

function asStr(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}
function asNum(v: unknown): number | null {
  return typeof v === 'number' ? v : null;
}
function asBool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

const EMPTY_DRAFT: UserRow = {
  userId: 0,
  email: '',
  displayName: '',
  firstName: null,
  lastName: null,
  officerCode: null,
  branchCode: null,
  regionCode: null,
  entraObjectId: null,
  department: null,
  jobTitle: null,
  officeLocation: null,
  mobilePhone: null,
  businessPhone: null,
  supervisorUserId: null,
  photoBase64: null,
  photoUpdatedAt: null,
  isActive: true,
  isOfficer: false,
  trainingMode: false,
  missingOfficerCode: true,
  isInSandboxMode: false,
  isTestIdentitiesOnly: false,
  isUATEnrolled: false,
  hasTestLiveAccess: false,
  testRecipientEmail: null,
  testRecipientPhone: null,
  operatorNumber: null,
  roleCodes: [],
  storedRoleCodes: [],
};

function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function UsersPage(): JSX.Element {
  const client = useApiClient();
  const { user: me } = useAuth();
  // Server rule: only Global Admin (SuperAdmin) can REMOVE a role
  // (DELETE /v3/admin/user-role-assignments is SuperAdmin-gated). Case-insensitive
  // to match the server's HasRole.
  const callerIsSuperAdmin = (me?.roles ?? []).some((r) => r.toLowerCase() === 'superadmin');
  const [rawRows, setRows] = useState<ReadonlyArray<UserRow> | null>(null);
  // Full catalog (active + inactive) — needed to spell held codes canonically.
  const [catalog, setCatalog] = useState<ReadonlyArray<CatalogRole>>([]);
  const roles = useMemo(
    () => catalog.filter((r) => r.isActive).slice().sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999)),
    [catalog],
  );
  // Rows with role codes in catalog spelling (case-insensitive dedupe), so a
  // stored "ADMIN" and "Admin" never show as two roles or as an unknown one.
  const rows = useMemo<ReadonlyArray<UserRow> | null>(
    () => rawRows?.map((u) => ({ ...u, roleCodes: canonicalCodes(catalog, u.storedRoleCodes) })) ?? null,
    [rawRows, catalog],
  );
  const [plan, setPlan] = useState<SavePlan | null>(null);
  const [officerNoteShown, setOfficerNoteShown] = useState<boolean>(false);
  const [history, setHistory] = useState<ReadonlyArray<UserAccessAuditRow> | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [confirmBulk, setConfirmBulk] = useState<boolean | null>(null);
  const [fixingSpelling, setFixingSpelling] = useState<boolean>(false);
  const [branches, setBranches] = useState<ReadonlyArray<BranchOption>>([]);
  const [regions, setRegions] = useState<ReadonlyArray<RegionOption>>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  // v0.8.12 — quick filters above the users table.
  // Role chips: any-of OR-filter. Empty set = no role constraint.
  const [roleFilters, setRoleFilters] = useState<Set<string>>(new Set()); // lowercase role codes
  // Status select: single-choice. v0.8.13 — default to 'active' so the
  // list hides inactive users unless the admin asks for them (parallel to
  // the Ignored pattern — discoverable header pill counts the hidden
  // population).
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'missing-initials'>('active');
  // v0.32 (Bill 2026-06-18, BACKLOG-0579) — user-mode-flag quick filter. Folds
  // the at-a-glance matrix from the retired ROP-teams "User Mode Flags" screen
  // in here (its per-user editing already lives in the Edit modal's Test &
  // Sandbox section), so the dedicated screen could be purged with no capability
  // loss.
  const [modeFilter, setModeFilter] = useState<'all' | 'sandbox' | 'uat' | 'testlive' | 'none'>('all');
  // v0.8.12 — bulk select state. Set of userIds.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<boolean>(false);
  const [bulkBanner, setBulkBanner] = useState<string | null>(null);
  // v0.8.13 — after an in-modal Sync-from-Entra apply, the underlying
  // editing row is stale. Stamp the userId here, then a useEffect watches
  // rows for the refreshed snapshot and replaces editing in place.
  const [awaitingRefreshUserId, setAwaitingRefreshUserId] = useState<number | null>(null);

  // v0.9.0 — product-category enablement (DDA/SAV/CD/IRA/BOX). Officer
  // role gates: when the user holds Officer, all 5 are default-on
  // server-side. Loaded on Edit-open; saved on Save.
  const ALL_CATEGORIES: ReadonlyArray<{ code: 'DDA' | 'SAV' | 'CD' | 'IRA' | 'BOX'; label: string; hint: string }> = [
    { code: 'DDA', label: 'Checking',    hint: 'Demand Deposit Accounts' },
    { code: 'SAV', label: 'Savings',     hint: 'Regular + MMA' },
    { code: 'CD',  label: 'CD',          hint: 'Certificates of Deposit' },
    { code: 'IRA', label: 'IRA',         hint: 'Individual Retirement Accounts' },
    { code: 'BOX', label: 'Safe Deposit',hint: 'Safe Deposit Boxes' },
  ];
  const [enabledCategories, setEnabledCategories] = useState<Set<string>>(new Set());
  const [originalCategories, setOriginalCategories] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [originalRoles, setOriginalRoles] = useState<ReadonlyArray<string>>([]);
  const [showEntraPicker, setShowEntraPicker] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UserRow | null>(null);
  const [showTestMode, setShowTestMode] = useState<boolean>(false);
  // Bank-wide Test mode (SystemSetting pilot.testIdentitiesOnly.enabled, seed 360) shown
  // beside the per-user toggle. null = not loaded / unreadable (nothing shown).
  const [testModeGlobal, setTestModeGlobal] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await client.retail.config.systemSettingsRows();
        if (cancelled || !resp.success || !Array.isArray(resp.data)) return;
        const row = (resp.data as unknown as ReadonlyArray<{ settingKey?: string; settingValue?: string | null }>)
          .find((r) => r.settingKey === 'pilot.testIdentitiesOnly.enabled');
        setTestModeGlobal(['true', '1', 'yes', 'on'].includes((row?.settingValue ?? '').trim().toLowerCase()));
      } catch {
        /* leave null — the toggle still works; the server is the authority */
      }
    })();
    return () => { cancelled = true; };
  }, [client]);
  const [refreshNonce, setRefreshNonce] = useState<number>(0);
  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  // Per-user sync modal state
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncDiff, setSyncDiff] = useState<SingleUserDiffResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncAccepted, setSyncAccepted] = useState<Set<string>>(new Set());

  // Subordinate overrides for the currently-edited user
  const [supervisorSubordinates, setSupervisorSubordinates] = useState<
    Array<{ userId: number; email: string; source: 'override-add' }>
  >([]);

  // Load lookups + users
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [b, r, ro] = await Promise.all([
          client.retail.config.branches(),
          client.retail.config.regions(),
          client.retail.config.roles(),
        ]);
        if (cancelled) return;
        if (b.success && Array.isArray(b.data)) {
          // /v3/config/branches reshapes to client-DTO names
          // (branchCode/branchName), not canonical DB column names. Read
          // either to be tolerant if the contract flips back.
          setBranches(
            (b.data as Array<{ code?: string; name?: string; branchCode?: string; branchName?: string }>)
              .map((x) => ({
                code: (x.branchCode ?? x.code) ?? '',
                name: (x.branchName ?? x.name ?? x.branchCode ?? x.code) ?? '',
              }))
              .filter((x) => x.code),
          );
        }
        if (r.success && Array.isArray(r.data)) {
          // /v3/config/regions reshapes to regionCode/regionName too.
          setRegions(
            (r.data as Array<{ code?: string; name?: string; regionCode?: string; regionName?: string }>)
              .map((x) => ({
                code: (x.regionCode ?? x.code) ?? '',
                name: (x.regionName ?? x.name ?? x.regionCode ?? x.code) ?? '',
              }))
              .filter((x) => x.code),
          );
        }
        if (ro.success && Array.isArray(ro.data)) {
          setCatalog(
            (ro.data as Array<{ code?: string; name?: string; isActive?: boolean; sortOrder?: number | null; description?: string | null }>)
              .filter((x) => x.code)
              .map((x) => ({
                code: x.code!,
                name: x.name ?? x.code!,
                isActive: x.isActive !== false,
                sortOrder: x.sortOrder ?? null,
                description: x.description ?? null,
              })),
          );
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[admin-panel] lookup load failed', e);
      }
    })();
    return () => { cancelled = true };
  }, [client]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await client.retail.config.users.list();
        if (cancelled) return;
        if (!resp.success) {
          setError(resp.error ?? 'Failed to load users.');
          return;
        }
        const data = resp.data as unknown;
        const env = data && typeof data === 'object' && !Array.isArray(data)
          ? (data as UserRolesEnvelope)
          : null;
        const usersArr = env?.users ?? (Array.isArray(data) ? (data as Record<string, unknown>[]) : []);
        const rolesArr = env?.roles ?? [];

        const rolesByUser = new Map<number, string[]>();
        for (const r of rolesArr) {
          if (!r) continue;
          const uid = typeof r.userId === 'number' ? r.userId : undefined;
          const code = r.roleCode;
          if (uid === undefined || !code) continue;
          const arr = rolesByUser.get(uid) ?? [];
          if (!arr.includes(code)) arr.push(code);
          rolesByUser.set(uid, arr);
        }

        const mapped: UserRow[] = usersArr.map((raw) => {
          const u = raw as Record<string, unknown>;
          const userId = asNum(u['userId']) ?? 0;
          return {
            userId,
            email: asStr(u['email']) ?? '',
            displayName: asStr(u['displayName']) ?? asStr(u['email']) ?? '(unknown)',
            firstName: asStr(u['firstName']),
            lastName: asStr(u['lastName']),
            officerCode: asStr(u['officerCode']),
            branchCode: asStr(u['branchCode']),
            regionCode: asStr(u['regionCode']),
            entraObjectId: asStr(u['entraObjectId']),
            department: asStr(u['department']),
            jobTitle: asStr(u['jobTitle']),
            officeLocation: asStr(u['officeLocation']),
            mobilePhone: asStr(u['mobilePhone']),
            businessPhone: asStr(u['businessPhone']),
            supervisorUserId: asNum(u['supervisorUserId']),
            photoBase64: asStr(u['photoBase64']),
            photoUpdatedAt: asStr(u['photoUpdatedAt']),
            isActive: asBool(u['isActive'], true),
            isOfficer: asBool(u['isOfficer'], false),
            trainingMode: asBool(u['trainingMode'], false),
            missingOfficerCode: asBool(u['missingOfficerCode'], false),
            isInSandboxMode: asBool(u['isInSandboxMode'], false),
            isTestIdentitiesOnly: asBool(u['isTestIdentitiesOnly'], false),
            isUATEnrolled: asBool(u['isUATEnrolled'], false),
            hasTestLiveAccess: asBool(u['hasTestLiveAccess'], false),
            testRecipientEmail: asStr(u['testRecipientEmail']),
            testRecipientPhone: asStr(u['testRecipientPhone']),
            operatorNumber: asStr(u['operatorNumber']),
            roleCodes: rolesByUser.get(userId) ?? [],
            storedRoleCodes: rolesByUser.get(userId) ?? [],
          };
        });
        setRows(mapped);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true };
  }, [client, refreshNonce]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    let out: ReadonlyArray<UserRow> = rows;
    if (filter.trim()) {
      const n = filter.toLowerCase();
      out = out.filter((u) =>
        u.email.toLowerCase().includes(n) ||
        u.displayName.toLowerCase().includes(n) ||
        (u.officerCode ?? '').toLowerCase().includes(n) ||
        (u.branchCode ?? '').toLowerCase().includes(n) ||
        (u.jobTitle ?? '').toLowerCase().includes(n) ||
        (u.department ?? '').toLowerCase().includes(n),
      );
    }
    if (roleFilters.size > 0) {
      out = out.filter((u) =>
        u.roleCodes.some((c) => roleFilters.has(c.toLowerCase())),
      );
    }
    if (statusFilter !== 'all') {
      out = out.filter((u) => {
        const isMissing = u.missingOfficerCode && !u.officerCode;
        if (statusFilter === 'active') return u.isActive && !isMissing;
        if (statusFilter === 'inactive') return !u.isActive;
        if (statusFilter === 'missing-initials') return isMissing;
        return true;
      });
    }
    if (modeFilter !== 'all') {
      out = out.filter((u) => {
        if (modeFilter === 'sandbox') return u.isInSandboxMode;
        if (modeFilter === 'uat') return u.isUATEnrolled;
        if (modeFilter === 'testlive') return u.hasTestLiveAccess;
        if (modeFilter === 'none') return !u.isInSandboxMode && !u.isUATEnrolled && !u.hasTestLiveAccess;
        return true;
      });
    }
    return out;
  }, [rows, filter, roleFilters, statusFilter, modeFilter]);

  // Lookups for the supervisor link
  const usersByUserId = useMemo(() => {
    const m = new Map<number, UserRow>();
    if (rows) for (const u of rows) m.set(u.userId, u);
    return m;
  }, [rows]);

  const branchOpts = useMemo(
    () => branches.map((b) => ({ value: b.code, label: `${b.code} — ${b.name}` })),
    [branches],
  );
  const regionOpts = useMemo(
    () => regions.map((r) => ({ value: r.code, label: `${r.code} — ${r.name}` })),
    [regions],
  );
  // v0.8.11 — role codes can arrive in either PascalCase (from
  // /v3/config/roles catalog seeds) or UPPERCASE (from ApplyGlobalSuggest
  // which uppercases on write). Match case-insensitively everywhere the
  // editing state is queried.
  const hasRoleCi = (code: string) =>
    (editing?.roleCodes ?? []).some((c) => c.toLowerCase() === code.toLowerCase());
  const showManagerRegion = hasRoleCi('Manager');
  const isSupervisor = hasRoleCi('Supervisor');

  // ── Save handler (single user upsert + role delta) ────────────────
  //
  // 2026-10-07 — Save first shows a plain summary ("adds X, removes Y, changes
  // Z") and writes only after the admin confirms. Role codes are compared
  // case-insensitively against the catalog; the server stores the catalog
  // spelling and removes every stored spelling of a removed role.
  const sameCats =
    enabledCategories.size === originalCategories.size &&
    Array.from(enabledCategories).every((c) => originalCategories.has(c));

  const buildPlan = useCallback((): SavePlan | null => {
    if (!editing) return null;
    const wantLower = new Set(editing.roleCodes.map((c) => c.toLowerCase()));
    const haveLower = new Set(originalRoles.map((c) => c.toLowerCase()));
    const adds = orderAdds(editing.roleCodes.filter((c) => !haveLower.has(c.toLowerCase())));
    const removes = orderRemoves(originalRoles.filter((c) => !wantLower.has(c.toLowerCase())));
    const before = rows?.find((u) => u.userId === editing.userId) ?? null;
    const fieldChanges: string[] = [];
    if (!before) {
      fieldChanges.push('Add this person to ROP');
    } else {
      const cmp = (label: string, a: unknown, b: unknown, fmt: (v: unknown) => string = (v) => (v == null || v === '' ? '(none)' : String(v))) => {
        if ((a ?? null) !== (b ?? null)) fieldChanges.push(`${label}: ${fmt(a)} → ${fmt(b)}`);
      };
      const yesNo = (v: unknown) => (v ? 'on' : 'off');
      if (before.isActive !== editing.isActive) fieldChanges.push(editing.isActive ? 'Reactivate the account' : 'Deactivate the account — every role stops working');
      cmp('Operator initials', before.officerCode, editing.officerCode);
      cmp('Branch', before.branchCode, editing.branchCode);
      cmp('Region', before.regionCode, editing.regionCode);
      // Mode controls (W7, Bill 2026-10-07): Training = IsInSandboxMode; Test = IsTestIdentitiesOnly.
      cmp('Training mode', before.isInSandboxMode, editing.isInSandboxMode, yesNo);
      cmp('Test mode', before.isTestIdentitiesOnly, editing.isTestIdentitiesOnly, yesNo);
      cmp('UAT enrolled', before.isUATEnrolled, editing.isUATEnrolled, yesNo);
      cmp('Test-Live access', before.hasTestLiveAccess, editing.hasTestLiveAccess, yesNo);
      if ((before.testRecipientEmail ?? null) !== (editing.testRecipientEmail ?? null)) fieldChanges.push('Test recipient e-mail changed');
      if ((before.testRecipientPhone ?? null) !== (editing.testRecipientPhone ?? null)) fieldChanges.push('Test recipient phone changed');
      if (!before.entraObjectId) {
        cmp('Display name', before.displayName, editing.displayName);
      }
    }
    const officerAfter = editing.roleCodes.some((c) => c.toLowerCase() === 'officer');
    return { adds, removes, fieldChanges, categoriesChanged: officerAfter && !sameCats };
  }, [editing, originalRoles, rows, sameCats]);

  const onSave = useCallback((): void => {
    setSaveError(null);
    const p = buildPlan();
    if (!p) return;
    if (p.removes.length > 0 && !callerIsSuperAdmin) {
      setSaveError(`Only a Global Admin can remove a role (${p.removes.map((c) => roleLabel(c)).join(', ')}). Re-check those boxes or ask a Global Admin.`);
      return;
    }
    if (editing && !editing.email.trim()) { setSaveError('E-mail is required.'); return; }
    if (editing && !editing.displayName.trim()) { setSaveError('Display name is required.'); return; }
    if (p.adds.length === 0 && p.removes.length === 0 && p.fieldChanges.length === 0 && !p.categoriesChanged) {
      setEditing(null);
      return;
    }
    setPlan(p);
  }, [buildPlan, callerIsSuperAdmin, editing]);

  const executeSave = useCallback(async (p: SavePlan): Promise<void> => {
    if (!editing) return;
    setSaving(true);
    setSaveError(null);
    try {
      const isNew = !editing.userId;
      const body = {
        userId: editing.userId || undefined,
        email: editing.email.trim(),
        displayName: editing.displayName.trim(),
        firstName: editing.firstName?.trim() || null,
        lastName: editing.lastName?.trim() || null,
        officerCode: editing.officerCode?.trim() || null,
        branchCode: editing.branchCode?.trim() || null,
        regionCode: editing.regionCode?.trim() || null,
        isActive: editing.isActive,
        isOfficer: editing.isOfficer,
        // Legacy TrainingMode column is read by nothing; kept in sync with the real switch.
        trainingMode: editing.isInSandboxMode,
        isInSandboxMode: editing.isInSandboxMode,
        isTestIdentitiesOnly: editing.isTestIdentitiesOnly,
        isUATEnrolled: editing.isUATEnrolled,
        hasTestLiveAccess: editing.hasTestLiveAccess,
        testRecipientEmail: editing.testRecipientEmail?.trim() || null,
        testRecipientPhone: editing.testRecipientPhone?.trim() || null,
        // Entra link + Entra-owned attributes. The server uses these on create only
        // and never overwrites an Entra-linked person's name from here.
        entraObjectId: editing.entraObjectId ?? undefined,
        jobTitle: isNew ? editing.jobTitle ?? undefined : undefined,
        department: isNew ? editing.department ?? undefined : undefined,
        officeLocation: isNew ? editing.officeLocation ?? undefined : undefined,
      };
      const resp = (await client.retail.admin.users.upsert(body)) as {
        success?: boolean; error?: string; data?: { userId?: number };
      };
      if (resp?.success === false) {
        setSaveError(resp.error ?? 'Save failed.');
        return;
      }
      const userId = editing.userId || resp.data?.userId;
      if (userId) {
        const assignmentsApi = (client.retail.admin as unknown as {
          userRoleAssignments: {
            upsert(b: { userId: number; roleCode: string }): Promise<{ success?: boolean; error?: string; code?: string }>;
            remove(userId: string, roleCode: string): Promise<{ success?: boolean; error?: string }>;
          };
        }).userRoleAssignments;

        const done: string[] = [];
        for (const code of p.adds) {
          try {
            const ar = await assignmentsApi.upsert({ userId, roleCode: code });
            if (ar?.success === false) throw new Error(ar.error ?? 'unknown');
            done.push(`added ${roleLabel(code)}`);
          } catch (e) {
            setSaveError(`Could not add ${roleLabel(code)}: ${errText(e)}${done.length ? ` (already done: ${done.join(', ')})` : ''}`);
            setPlan(null);
            refresh();
            return;
          }
        }
        for (const code of p.removes) {
          // Remove by every stored spelling of this role (server matches case-insensitively).
          const stored = editing.storedRoleCodes.find((s) => s.toLowerCase() === code.toLowerCase()) ?? code;
          try {
            const dr = await assignmentsApi.remove(String(userId), stored);
            if (dr?.success === false) throw new Error(dr.error ?? 'unknown');
            done.push(`removed ${roleLabel(code)}`);
          } catch (e) {
            setSaveError(`Could not remove ${roleLabel(code)}: ${errText(e)}${done.length ? ` (already done: ${done.join(', ')})` : ''}`);
            setPlan(null);
            refresh();
            return;
          }
        }

        // v0.9.0 — persist product-category enablement if the user holds
        // (or was just granted) Officer. Granting Officer seeds all five
        // server-side; only POST when the admin changed the set.
        if (p.categoriesChanged) {
          try {
            const cr = await client.retail.admin.replaceUserProductCategories(userId, {
              categoryCodes: Array.from(enabledCategories),
            });
            if (cr.success === false) throw new Error(cr.error ?? cr.code ?? 'unknown');
          } catch (e) {
            setSaveError(`Roles saved, but product categories were not: ${errText(e)}`);
            setPlan(null);
            refresh();
            return;
          }
        }
      }
      setPlan(null);
      setEditing(null);
      refresh();
    } catch (e) {
      setSaveError(errText(e));
      setPlan(null);
    } finally {
      setSaving(false);
    }
  }, [editing, enabledCategories, client, refresh]);

  // Re-store held roles in the catalog spelling (e.g. "RISKMANAGER" → "RiskManager").
  // The server keeps the original grant date; the Effective-access panel shows
  // what changes. Kept explicit because some apps read the stored spelling.
  const fixSpelling = useCallback(async (codes: ReadonlyArray<string>): Promise<void> => {
    if (!editing?.userId) return;
    setFixingSpelling(true);
    setSaveError(null);
    try {
      for (const code of codes) {
        await (client.retail.admin as unknown as {
          userRoleAssignments: { upsert(b: { userId: number; roleCode: string }): Promise<unknown> };
        }).userRoleAssignments.upsert({ userId: editing.userId, roleCode: canonicalCode(catalog, code) });
      }
      setAwaitingRefreshUserId(editing.userId);
      refresh();
    } catch (e) {
      setSaveError(`Could not fix the spelling: ${errText(e)}`);
    } finally {
      setFixingSpelling(false);
    }
  }, [client, editing, catalog, refresh]);

  // Access history (dbo.UserAccessAudit) — loaded when the section is opened.
  useEffect(() => {
    setHistory(null);
    setHistoryError(null);
    if (!showHistory || !editing?.userId) return;
    let cancelled = false;
    void (async () => {
      try {
        const resp = await client.retail.admin.userAccessHistory(editing.userId);
        if (cancelled) return;
        if (resp.success) setHistory(resp.data);
        else setHistoryError(resp.error ?? 'Could not load history.');
      } catch (e) {
        if (!cancelled) setHistoryError(errText(e));
      }
    })();
    return () => { cancelled = true };
  }, [client, showHistory, editing?.userId, refreshNonce]);

  const onDelete = useCallback(async () => {
    if (!confirmDelete) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = (await client.retail.admin.users.remove(String(confirmDelete.userId))) as { success?: boolean; error?: string };
      if (resp?.success === false) { setSaveError(resp.error ?? 'Delete failed.'); return }
      setConfirmDelete(null);
      refresh();
    } catch (e) {
      setSaveError(errText(e));
    } finally {
      setSaving(false);
    }
  }, [confirmDelete, client, refresh]);

  const onPickEntraCandidate = useCallback((c: EntraUserCandidate) => {
    setEditing({
      ...EMPTY_DRAFT,
      email: c.mail?.toLowerCase() ?? c.userPrincipalName.toLowerCase(),
      displayName: c.displayName,
      firstName: c.givenName ?? null,
      lastName: c.surname ?? null,
      department: (c as { department?: string | null }).department ?? null,
      jobTitle: c.jobTitle ?? null,
      officeLocation: c.officeLocation ?? null,
      entraObjectId: c.id,
    });
    setOriginalRoles([]);
    setOfficerNoteShown(false);
    setShowHistory(false);
    setShowEntraPicker(false);
  }, []);

  // ── Per-user Sync modal ─────────────────────────────────────────────
  const openSyncModal = useCallback(async (u: UserRow) => {
    setEditing(u); // make sure context's there
    setSyncOpen(true);
    setSyncDiff(null);
    setSyncError(null);
    setSyncAccepted(new Set());
    setSyncLoading(true);
    try {
      const resp = await client.retail.admin.syncSingleUserDiff(u.userId);
      if (!resp.success) {
        setSyncError(resp.error ?? resp.code ?? 'Diff failed.');
      } else {
        setSyncDiff(resp.data);
      }
    } catch (e) {
      setSyncError(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncLoading(false);
    }
  }, [client]);

  const applySync = useCallback(async () => {
    if (!editing || !syncDiff) return;
    setSyncLoading(true);
    setSyncError(null);
    try {
      const resp = await client.retail.admin.syncSingleUserApply(editing.userId, {
        acceptedFields: Array.from(syncAccepted),
      });
      if (!resp.success) {
        setSyncError(resp.error ?? resp.code ?? 'Apply failed.');
        return;
      }
      setSyncOpen(false);
      // v0.8.13 (Bill 2026-05-15) — keep the Edit modal open AND refresh
      // its data after the apply succeeds. refresh() reloads rows; the
      // useEffect below catches the new row and patches editing in place.
      setAwaitingRefreshUserId(editing.userId);
      refresh();
    } catch (e) {
      setSyncError(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncLoading(false);
    }
  }, [client, editing, syncDiff, syncAccepted, refresh]);

  // v0.8.13 — refresh `editing` from `rows` once the latter updates after
  // an in-modal Sync-from-Entra apply. One-shot via awaitingRefreshUserId.
  useEffect(() => {
    if (awaitingRefreshUserId === null || !rows) return;
    const fresh = rows.find((u) => u.userId === awaitingRefreshUserId);
    if (fresh) {
      setEditing(fresh);
      setOriginalRoles(fresh.roleCodes);
      setAwaitingRefreshUserId(null);
    }
  }, [rows, awaitingRefreshUserId]);

  // v0.9.0 — load product categories whenever the Edit modal opens on an
  // existing user. Empty for brand-new users (Officer hasn't been granted
  // and saved yet). Server treats an absent category row as disabled.
  useEffect(() => {
    if (!editing || editing.userId === 0) {
      setEnabledCategories(new Set());
      setOriginalCategories(new Set());
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const resp = await client.retail.admin.listUserProductCategories(editing.userId);
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          const set = new Set(resp.data.map((r) => r.categoryCode.toUpperCase()));
          setEnabledCategories(set);
          setOriginalCategories(new Set(set));
        }
      } catch { /* leave empty */ }
    })();
    return () => { cancelled = true };
  }, [client, editing?.userId]);

  // ── Subordinate overrides — load on Edit modal open for Supervisor ──
  useEffect(() => {
    if (!editing || !isSupervisor || editing.userId === 0) {
      setSupervisorSubordinates([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const resp = await client.retail.admin.listSubordinateOverrides(editing.userId);
        if (cancelled) return;
        if (resp.success) {
          setSupervisorSubordinates(
            (resp.data as ReadonlyArray<SubordinateOverrideRow>)
              .filter((r) => r.overrideKind === 'add')
              .map((r) => ({ userId: r.subordinateUserId, email: r.subordinateEmail, source: 'override-add' as const })),
          );
        }
      } catch {
        /* leave empty */
      }
    })();
    return () => { cancelled = true };
  }, [client, editing, isSupervisor]);

  const toggleSelected = useCallback((userId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  }, []);
  const toggleAllVisible = useCallback((visibleIds: ReadonlyArray<number>, select: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of visibleIds) {
        if (select) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const bulkSetActive = useCallback(async (active: boolean) => {
    if (selectedIds.size === 0 || !rows) return;
    setBulkBusy(true); setBulkBanner(null);
    const targets = rows.filter((u) => selectedIds.has(u.userId));
    let ok = 0; const failures: string[] = [];
    for (const u of targets) {
      try {
        const resp = (await client.retail.admin.users.upsert({
          userId: u.userId,
          email: u.email,
          displayName: u.displayName,
          firstName: u.firstName,
          lastName: u.lastName,
          officerCode: u.officerCode,
          branchCode: u.branchCode,
          regionCode: u.regionCode,
          isActive: active,
          trainingMode: u.isInSandboxMode,
          isInSandboxMode: u.isInSandboxMode,
          isTestIdentitiesOnly: u.isTestIdentitiesOnly,
          isUATEnrolled: u.isUATEnrolled,
          hasTestLiveAccess: u.hasTestLiveAccess,
          testRecipientEmail: u.testRecipientEmail ?? undefined,
          testRecipientPhone: u.testRecipientPhone ?? undefined,
          entraObjectId: u.entraObjectId ?? undefined,
        })) as { success?: boolean; error?: string };
        if (resp?.success === false) failures.push(`${u.email}: ${resp.error ?? 'unknown'}`);
        else ok++;
      } catch (e) {
        failures.push(`${u.email}: ${errText(e)}`);
      }
    }
    setBulkBanner(
      `${active ? 'Activated' : 'Deactivated'} ${ok}/${targets.length}` +
      (failures.length > 0 ? ` — ${failures.length} failure(s): ${failures.slice(0, 3).join('; ')}${failures.length > 3 ? '…' : ''}` : ''),
    );
    clearSelection();
    refresh();
    setBulkBusy(false);
  }, [client, rows, selectedIds, clearSelection, refresh]);

  const columns: ReadonlyArray<DataTableColumn<UserRow>> = useMemo(() => [
    {
      header: '',
      width: 32,
      cell: (u) => (
        <input
          type="checkbox"
          aria-label={`Select ${u.email}`}
          checked={selectedIds.has(u.userId)}
          onChange={() => toggleSelected(u.userId)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      header: '',
      width: 48,
      cell: (u) => (
        <div className="user-avatar" aria-hidden>
          {u.photoBase64 ? (
            <img alt="" src={`data:image/jpeg;base64,${u.photoBase64}`} />
          ) : (
            <span>{initials(u.displayName)}</span>
          )}
        </div>
      ),
    },
    {
      header: 'Email',
      cell: (u) => <span className="cell-strong">{u.email}</span>,
      sortKey: (u) => u.email.toLowerCase(),
    },
    {
      header: 'Display Name',
      cell: (u) => u.displayName,
      sortKey: (u) => u.displayName.toLowerCase(),
    },
    {
      header: 'Operator',
      cell: (u) => u.officerCode ? <code className="cell-mono">{u.officerCode}</code> : <span className="cell-dim">—</span>,
      sortKey: (u) => u.officerCode ?? null,
    },
    {
      header: 'Branch',
      cell: (u) => u.branchCode ?? <span className="cell-dim">—</span>,
      sortKey: (u) => u.branchCode ?? null,
    },
    {
      header: 'Title',
      cell: (u) => u.jobTitle ?? <span className="cell-dim">—</span>,
      sortKey: (u) => u.jobTitle?.toLowerCase() ?? null,
    },
    {
      header: 'Roles',
      cell: (u) => (
        <div className="pill-stack">
          {u.roleCodes.length === 0 ? (
            <Pill tone="muted">none</Pill>
          ) : (
            u.roleCodes.map((r) => (
              <span key={r} title={r}>
                <Pill tone={catalog.some((c) => c.code === r && c.isActive) ? 'info' : 'muted'}>
                  {roleLabel(r, catalog.find((c) => c.code === r)?.name)}
                </Pill>
              </span>
            ))
          )}
        </div>
      ),
      sortKey: (u) => u.roleCodes.slice().sort().join(',').toLowerCase() || null,
    },
    {
      header: 'Status',
      cell: (u) => (
        <div className="pill-stack">
          {!u.isActive && <Pill tone="muted">inactive</Pill>}
          {/* v0.8.11 (Bill 2026-05-15) — only flag when the flag is true AND
              no code is actually set. Suppresses false positives on users
              who already have operator initials but inherited a stale
              MissingOfficerCode=true (legacy data, default-on at create). */}
          {u.missingOfficerCode && !u.officerCode && <Pill tone="warn">missing operator initials</Pill>}
          {u.isActive && (!u.missingOfficerCode || u.officerCode) && <Pill tone="ok">active</Pill>}
        </div>
      ),
      // sort ordering: active > missing-initials > inactive
      sortKey: (u) => {
        if (!u.isActive) return 'z-inactive';
        if (u.missingOfficerCode && !u.officerCode) return 'm-missing';
        return 'a-active';
      },
    },
    {
      header: '',
      width: 120,
      align: 'right',
      cell: (u) => (
        <div className="row-actions">
          <Button variant="ghost" size="sm" onClick={() => {
            setSaveError(null);
            setEditing(u);
            setOriginalRoles(u.roleCodes);
            setShowTestMode(false);
            setOfficerNoteShown(false);
            setShowHistory(false);
          }}>
            <Icon name="pencil" /> Edit
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setSaveError(null); setConfirmDelete(u) }}>
            <Icon name="trash" />
          </Button>
        </div>
      ),
    },
  ], [selectedIds, toggleSelected, catalog]);

  if (error) return <Banner tone="err">Could not load users: {error}</Banner>;
  if (!rows) return <Spinner label="Loading users…" />;

  // Distinct role codes across all users — drives the role filter chips.
  // Drawn from ACTIVE users only so the chip set matches what the (now
  // default-active-only) list can actually show.
  const distinctRoleCodes = (() => {
    const s = new Map<string, string>(); // lowercase -> original-case label
    for (const u of rows) {
      if (!u.isActive) continue;
      for (const c of u.roleCodes) {
        const key = c.toLowerCase();
        if (!s.has(key)) s.set(key, c);
      }
    }
    return Array.from(s.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  })();

  const inactiveCount = rows.filter((u) => !u.isActive).length;
  const modeCounts = {
    sandbox: rows.filter((u) => u.isInSandboxMode).length,
    uat: rows.filter((u) => u.isUATEnrolled).length,
    testlive: rows.filter((u) => u.hasTestLiveAccess).length,
  };

  const visibleIds = (filtered ?? []).map((u) => u.userId);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = !allVisibleSelected && visibleIds.some((id) => selectedIds.has(id));

  const supervisorRow = editing?.supervisorUserId ? usersByUserId.get(editing.supervisorUserId) : null;

  // Roles held that are not offered as checkboxes (inactive catalog codes such as
  // the LOP roles) — shown read-only and never touched by Save.
  const heldInactive = (editing?.roleCodes ?? []).filter(
    (c) => !roles.some((r) => r.code.toLowerCase() === c.toLowerCase()),
  );
  // Stored spellings that differ from the catalog (e.g. "RISKMANAGER").
  const misspelled = (editing?.storedRoleCodes ?? [])
    .map((stored) => ({ stored, canonical: canonicalCode(catalog, stored) }))
    .filter((m) => m.stored !== m.canonical && catalog.some((r) => r.code === m.canonical));
  // Exact spellings after Save: kept roles keep their stored spelling; new
  // grants are stored in the catalog spelling (server, 2026-10-07).
  const predictedStored = (editing?.roleCodes ?? []).map(
    (c) => editing?.storedRoleCodes.find((s) => s.toLowerCase() === c.toLowerCase()) ?? c,
  );
  const access = editing
    ? effectiveAccess(predictedStored, {
        isActive: editing.isActive,
        entraLinked: !!editing.entraObjectId,
        officerCode: editing.officerCode,
        operatorNumber: editing.operatorNumber,
        missingOfficerCode: editing.missingOfficerCode,
        isInSandboxMode: editing.isInSandboxMode,
      })
    : [];

  return (
    <>
      <Card
        title={`Users — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter" data-tour="users-filter">
              <TextField label="" value={filter} onChange={setFilter}
                placeholder="Filter by email, name, operator initials, branch, title, department…" />
            </div>
            {inactiveCount > 0 && statusFilter !== 'inactive' && (
              <Button variant="ghost" onClick={() => setStatusFilter('inactive')}>
                <Icon name="user-slash" /> Inactive ({inactiveCount})
              </Button>
            )}
            {statusFilter === 'inactive' && (
              <Button variant="ghost" onClick={() => setStatusFilter('active')}>
                <Icon name="arrow-left" /> Back to active
              </Button>
            )}
            <Button variant="primary" onClick={() => { setSaveError(null); setShowEntraPicker(true) }}>
              <Icon name="user-add" weight="solid" /> Add user
            </Button>
          </div>
        }
      >
        {/* v0.8.12 — quick filters strip + bulk action bar. */}
        <div className="users-filter-strip" data-tour="users-filter-strip">
          <div className="users-filter-strip__group">
            <span className="cell-dim">Roles:</span>
            <button
              type="button"
              className={`pill pill--info pill--clickable${roleFilters.size === 0 ? ' pill--active' : ' pill--inactive'}`}
              onClick={() => setRoleFilters(new Set())}
            >
              All
            </button>
            {distinctRoleCodes.map(([lc, label]) => {
              const active = roleFilters.has(lc);
              return (
                <button
                  key={lc}
                  type="button"
                  className={`pill pill--info pill--clickable${active ? ' pill--active' : ' pill--inactive'}`}
                  onClick={() => {
                    const next = new Set(roleFilters);
                    next.has(lc) ? next.delete(lc) : next.add(lc);
                    setRoleFilters(next);
                  }}
                >
                  {roleLabel(label, catalog.find((r) => r.code === label)?.name)}
                </button>
              );
            })}
          </div>
          <div className="users-filter-strip__group">
            <span className="cell-dim">Status:</span>
            <select
              className="users-filter-strip__select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              aria-label="Filter by status"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="missing-initials">Missing initials</option>
            </select>
          </div>
          {/* Mode flags — folds in the retired ROP-teams User Mode Flags
              at-a-glance view. Edit a user to change the flags (Test & Sandbox
              section). HasTestLiveAccess is SuperAdmin-only edit, server-enforced. */}
          <div className="users-filter-strip__group">
            <span className="cell-dim">Mode flags:</span>
            <select
              className="users-filter-strip__select"
              value={modeFilter}
              onChange={(e) => setModeFilter(e.target.value as typeof modeFilter)}
              aria-label="Filter by user mode flag"
            >
              <option value="all">Any</option>
              <option value="sandbox">Sandbox ({modeCounts.sandbox})</option>
              <option value="uat">UAT enrolled ({modeCounts.uat})</option>
              <option value="testlive">Test-Live ({modeCounts.testlive})</option>
              <option value="none">No flags</option>
            </select>
          </div>
        </div>

        {selectedIds.size > 0 && (
          <div className="users-bulk-bar">
            <span>
              <input
                type="checkbox"
                aria-label="Select all visible"
                checked={allVisibleSelected}
                ref={(el) => { if (el) el.indeterminate = someVisibleSelected }}
                onChange={() => toggleAllVisible(visibleIds, !allVisibleSelected)}
              />
              {' '}
              {selectedIds.size} selected
            </span>
            <Button variant="ghost" size="sm" disabled={bulkBusy} onClick={() => setConfirmBulk(true)}>
              Activate
            </Button>
            <Button variant="ghost" size="sm" disabled={bulkBusy} onClick={() => setConfirmBulk(false)}>
              Deactivate
            </Button>
            <Button variant="ghost" size="sm" disabled={bulkBusy} onClick={clearSelection}>
              Clear
            </Button>
          </div>
        )}
        {bulkBanner && <Banner tone="info">{bulkBanner}</Banner>}

        <DataTable<UserRow>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(u) => String(u.userId)}
          emptyState={<p className="empty">No users match the current filter.</p>}
        />
      </Card>

      {/* Add User — Entra picker */}
      <ModalShell
        open={showEntraPicker}
        title="Add user — choose from Entra"
        onClose={() => setShowEntraPicker(false)}
        width={560}
      >
        <p className="form-hint">
          Pick from the canonical identity source group. Already-added users are
          filtered out automatically.
        </p>
        <EntraUserPicker onSelect={onPickEntraCandidate} placeholder="Type a name…" />
        <div className="form-hint form-hint--spaced">
          Don&apos;t see who you&apos;re looking for?{' '}
          <button type="button" className="link" onClick={() => {
            setEditing({ ...EMPTY_DRAFT });
            setOriginalRoles([]);
            setShowEntraPicker(false);
          }}>
            Add manually
          </button>
        </div>
      </ModalShell>

      {/* Edit modal (2-column) */}
      <ModalShell
        open={editing !== null}
        title={editing && editing.userId ? `Edit user — ${editing.email}` : 'Add user'}
        onClose={() => (saving ? undefined : setEditing(null))}
        width={920}
        actions={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={onSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        {editing && (
          <>
            {saveError && <Banner tone="err">{saveError}</Banner>}

            {/* Header — avatar + name + Sync button */}
            <div className="user-edit-header">
              <div className="user-avatar user-avatar--lg" aria-hidden>
                {editing.photoBase64 ? (
                  <img alt="" src={`data:image/jpeg;base64,${editing.photoBase64}`} />
                ) : (
                  <span>{initials(editing.displayName || editing.email || '?')}</span>
                )}
              </div>
              <div className="user-edit-header__name">
                <h4>{editing.displayName || '(no name)'}</h4>
                <code className="cell-mono">{editing.email}</code>
              </div>
              <div className="user-edit-header__cta">
                {editing.userId > 0 && (
                  <Button variant="ghost" onClick={() => void openSyncModal(editing)}>
                    <Icon name="refresh" /> Sync from Entra
                  </Button>
                )}
              </div>
            </div>

            <div className="user-edit-grid">
              {/* LEFT — Entra-derived (read-only) */}
              <section className="user-edit-col">
                <h5>Managed by Entra (read-only — refresh via Sync from Entra)</h5>
                {!editing.entraObjectId && editing.userId === 0 ? (
                  <>
                    {/* Manual add (no Entra pick): the only time name + e-mail are typed here. */}
                    <Banner tone="warn">
                      Not linked to Entra. The Risk, SysOps and admin panels identify people by their
                      Entra account, so this person will be refused there until the link is made
                      (Sync from Entra after saving). Prefer &quot;Add user&quot; → choose from Entra.
                    </Banner>
                    <TextField label="Email" value={editing.email} required
                      onChange={(v) => setEditing({ ...editing, email: v.trim().toLowerCase() })} />
                    <TextField label="Display Name" value={editing.displayName} required
                      onChange={(v) => setEditing({ ...editing, displayName: v })} />
                  </>
                ) : (
                  <>
                    {!editing.entraObjectId && (
                      <Banner tone="warn">
                        Not linked to an Entra account — the Risk, SysOps and admin panels will refuse
                        this person until it is linked (Sync → Entra IDs).
                      </Banner>
                    )}
                    <ReadField label="Email" value={editing.email} />
                    <ReadField label="Display Name" value={editing.displayName} />
                    <div className="form-row">
                      <ReadField label="First Name" value={editing.firstName} />
                      <ReadField label="Last Name" value={editing.lastName} />
                    </div>
                  </>
                )}
                <ReadField label="Department" value={editing.department} />
                <ReadField label="Job Title" value={editing.jobTitle} />
                <ReadField label="Office Location" value={editing.officeLocation} />
                <div className="form-row">
                  <ReadField label="Mobile Phone" value={editing.mobilePhone} />
                  <ReadField label="Business Phone" value={editing.businessPhone} />
                </div>
                <ReadField
                  label="Supervisor"
                  value={supervisorRow ? `${supervisorRow.displayName} (${supervisorRow.email})` : null}
                />
              </section>

              {/* RIGHT — admin-controlled */}
              <section className="user-edit-col">
                <h5>Admin controls</h5>
                <TextField
                  label="Operator Initials"
                  value={editing.officerCode ?? ''}
                  onChange={(v) => setEditing({ ...editing, officerCode: v.trim().toUpperCase() || null })}
                  monospace maxLength={8}
                  hint="Validated server-side against Core Director on save."
                />
                <ReadField
                  label="Operator number (from the SysOps Core Operator Numbers list)"
                  value={editing.operatorNumber}
                />
                <div className="form-row">
                  <SelectField
                    label="Branch"
                    value={editing.branchCode ?? ''}
                    onChange={(v) => setEditing({ ...editing, branchCode: v || null })}
                    options={branchOpts}
                    placeholder="— select —"
                    hint="Auto-derived from Entra officeLocation on sync; admin override allowed."
                  />
                  <SelectField
                    label="Region"
                    value={editing.regionCode ?? ''}
                    onChange={(v) => setEditing({ ...editing, regionCode: v || null })}
                    options={regionOpts}
                    placeholder="— select —"
                    disabled={!showManagerRegion}
                    hint={showManagerRegion ? 'Manager scope; empty = All Regions.' : 'Only relevant for Manager role.'}
                  />
                </div>
                <BoolField label="Active" value={editing.isActive}
                  onChange={(v) => setEditing({ ...editing, isActive: v })}
                  hint="Inactive users keep history but can't sign in." />
                {/* ── Mode controls (W7, Bill 2026-10-07): ONE Training toggle, ONE Test toggle.
                    Mutually exclusive — turning one on turns the other off; the server
                    refuses a save with both on (MODE_CONFLICT). If both were ever set,
                    Training wins for the cart (Sandbox) and the test-only rule still applies. */}
                <BoolField label="Training mode" value={editing.isInSandboxMode}
                  onChange={(v) => setEditing({ ...editing, isInSandboxMode: v, isTestIdentitiesOnly: v ? false : editing.isTestIdentitiesOnly })}
                  hint="Simulated: nothing reaches Core Director, LexisNexis or customers. For learning the app." />
                <BoolField label="Test mode" value={editing.isTestIdentitiesOnly}
                  onChange={(v) => setEditing({ ...editing, isTestIdentitiesOnly: v, isInSandboxMode: v ? false : editing.isInSandboxMode })}
                  hint="Real Core Director, test customers only (last name Test with a 666 SSN/TIN/ATIN, or a 000 CIF when no tax ID). Real customers are hidden and refused. For pilot and smoke testing." />
                {testModeGlobal !== null && (
                  <p className="cell-dim" role="status">
                    {testModeGlobal
                      ? 'Bank-wide Test mode is ON (System → Settings → pilot.testIdentitiesOnly.enabled): everyone is limited to test customers, whatever this toggle says.'
                      : 'Bank-wide Test mode is off (System → Settings → pilot.testIdentitiesOnly.enabled).'}
                  </p>
                )}

                {/* Roles — one checkbox per business role (2026-10-07). Business
                    name first, catalog code second, what it grants underneath. */}
                <div className="form-field">
                  <span className="form-field__label">Roles</span>
                  <div className="role-grid role-grid--wide">
                    {roles.length === 0 && <span className="cell-dim">No active roles loaded.</span>}
                    {roles.map((r) => {
                      const checked = editing.roleCodes.some((c) => c.toLowerCase() === r.code.toLowerCase());
                      const heldBefore = originalRoles.some((c) => c.toLowerCase() === r.code.toLowerCase());
                      const lockedOn = checked && heldBefore && !callerIsSuperAdmin;
                      return (
                        <label key={r.code} className={`role-grid__cell${checked ? ' role-grid__cell--on' : ''}`}
                          title={lockedOn ? 'Only a Global Admin can remove a role.' : undefined}>
                          <input type="checkbox" checked={checked} disabled={lockedOn}
                            onChange={(e) => {
                              const on = e.target.checked;
                              if (on && r.code.toLowerCase() === 'officer' && !heldBefore) setOfficerNoteShown(true);
                              setEditing({
                                ...editing,
                                roleCodes: on
                                  ? [...editing.roleCodes.filter((c) => c.toLowerCase() !== r.code.toLowerCase()), r.code]
                                  : editing.roleCodes.filter((c) => c.toLowerCase() !== r.code.toLowerCase()),
                              });
                            }} />
                          <span>
                            <span className="role-grid__label">{roleLabel(r.code, r.name)}</span>
                            <code className="role-grid__code"> {r.code}</code>
                            <span className="role-grid__grants">{roleGrants(r.code, r.description)}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {heldInactive.length > 0 && (
                    <span className="form-field__hint">
                      Also holds (inactive roles, kept as they are): {heldInactive.map((c) => roleLabel(c, catalog.find((x) => x.code === c)?.name)).join(', ')}.
                    </span>
                  )}
                  {officerNoteShown && editing.roleCodes.some((c) => c.toLowerCase() === 'officer') && !originalRoles.some((c) => c.toLowerCase() === 'officer') && (
                    <Banner tone="info">
                      Officer lets this person open real accounts in the officer app.
                      {!(editing.officerCode && editing.operatorNumber)
                        ? ' Their Core Director initials and operator number must be on file first, or the grant will be refused.'
                        : ''}
                    </Banner>
                  )}
                  {misspelled.length > 0 && editing.userId > 0 && (
                    <Banner tone="warn">
                      Stored in a different spelling from the catalog: {misspelled.map((m) => `${m.stored} (should be ${m.canonical})`).join(', ')}.
                      Some apps match role names exactly and do not recognise these.{' '}
                      <Button size="sm" variant="ghost" disabled={fixingSpelling || saving}
                        onClick={() => void fixSpelling(misspelled.map((m) => m.canonical))}>
                        {fixingSpelling ? 'Fixing…' : 'Fix spelling now'}
                      </Button>
                      {' '}(saves immediately; check Effective access below first — in the officer app,
                      a correctly spelled Risk Officer or SysOps role outranks Officer.)
                    </Banner>
                  )}
                  <span className="form-field__hint">
                    {callerIsSuperAdmin
                      ? 'Disclosure Manager needs Risk Officer (granted first automatically when both are ticked). '
                      : 'You can add roles; only a Global Admin can remove one. '}
                    Supervisor subordinates show when Supervisor is held.
                  </span>
                </div>

                {/* Effective access — predicted from the roles as they will be stored. */}
                <div className="form-field">
                  <span className="form-field__label">Effective access after Save</span>
                  <ul className="access-list">
                    {access.map((a) => (
                      <li key={a.area} className="access-list__row">
                        <Pill tone={a.verdict === 'yes' ? 'ok' : a.verdict === 'partial' ? 'warn' : 'muted'}>
                          {a.verdict === 'yes' ? 'yes' : a.verdict === 'partial' ? 'limited' : 'no'}
                        </Pill>
                        <span><strong>{a.area}:</strong> {a.detail}</span>
                      </li>
                    ))}
                  </ul>
                  <span className="form-field__hint">
                    When it takes effect: the person must reopen the app (the officer app and the panels
                    read roles once when they open). On the server, a change applies at once on the instance
                    that saved it and within 5 minutes everywhere else.
                  </span>
                </div>

                {/* v0.9.0 — Product categories. Officer-gated. Default all-on
                    when Officer is granted (seeded server-side); admin can
                    uncheck individual categories. These gate jXchange
                    write/edit ops per JxOperationCategoryMap. */}
                <div className="form-field">
                  <span className="form-field__label">Product categories</span>
                  <div className="role-grid">
                    {ALL_CATEGORIES.map((cat) => {
                      const officerHeld = editing.roleCodes.some((c) => c.toLowerCase() === 'officer');
                      const checked = enabledCategories.has(cat.code);
                      return (
                        <label key={cat.code} className="role-grid__cell" style={{ opacity: officerHeld ? 1 : 0.5 }}>
                          <input
                            type="checkbox"
                            disabled={!officerHeld}
                            checked={checked}
                            onChange={(e) => {
                              const next = new Set(enabledCategories);
                              if (e.target.checked) next.add(cat.code);
                              else next.delete(cat.code);
                              setEnabledCategories(next);
                            }}
                          />
                          <span>
                            <code className="role-grid__code">{cat.code}</code>
                            <span className="role-grid__name"> — {cat.label}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <span className="form-field__hint">
                    {editing.roleCodes.some((c) => c.toLowerCase() === 'officer')
                      ? 'Gates jXchange write/edit operations per category. All categories default to ON when Officer role is granted; admin can uncheck individually. Primarily used to restrict IRA account opening to qualified officers.'
                      : 'Requires the Officer role. Grant Officer first to enable per-category access.'}
                  </span>
                </div>

                {/* Subordinates (Supervisor only) */}
                {isSupervisor && editing.userId > 0 && (
                  <div className="form-field">
                    <span className="form-field__label">Subordinates (manual additions)</span>
                    {supervisorSubordinates.length === 0 ? (
                      <span className="cell-dim">No manual subordinate-overrides yet. Entra `directReports` are auto-synced; this list shows admin-added exceptions.</span>
                    ) : (
                      <ul className="form-bullets">
                        {supervisorSubordinates.map((s) => (
                          <li key={s.userId}><code className="cell-mono">{s.email}</code></li>
                        ))}
                      </ul>
                    )}
                    <span className="form-field__hint">
                      Manage via the SyncTab &quot;Subordinate overrides&quot; tab (v0.8.0+).
                    </span>
                  </div>
                )}
              </section>
            </div>

            {/* Test/Sandbox section (collapsible) */}
            <div className="user-edit-testmode">
              <button type="button" className="link" onClick={() => setShowTestMode((v) => !v)}>
                {showTestMode ? '▾' : '▸'} Test &amp; Sandbox mode
              </button>
              {showTestMode && (
                <div className="user-edit-testmode__body">
                  <BoolField label="UAT enrolled" value={editing.isUATEnrolled}
                    onChange={(v) => setEditing({ ...editing, isUATEnrolled: v })}
                    hint="UAT-Live cart auto-open for enrolled features." />
                  <BoolField label="Has Test-Live access" value={editing.hasTestLiveAccess}
                    onChange={(v) => setEditing({ ...editing, hasTestLiveAccess: v })}
                    hint="SuperAdmin-only edit. Grants opt-in Test-Live mode per cart." />
                  <div className="form-row">
                    <TextField label="Test recipient email" value={editing.testRecipientEmail ?? ''}
                      onChange={(v) => setEditing({ ...editing, testRecipientEmail: v || null })} />
                    <TextField label="Test recipient phone" value={editing.testRecipientPhone ?? ''}
                      onChange={(v) => setEditing({ ...editing, testRecipientPhone: v || null })} />
                  </div>
                </div>
              )}
            </div>

            {/* Guided tours — dbo.UserTourProgress (2026-10-07). */}
            {editing.userId > 0 && <UserToursSection userId={editing.userId} />}

            {/* Access history — dbo.UserAccessAudit (2026-10-07). */}
            {editing.userId > 0 && (
              <div className="user-edit-testmode">
                <button type="button" className="link" onClick={() => setShowHistory((v) => !v)}>
                  {showHistory ? '▾' : '▸'} Access history
                </button>
                {showHistory && (
                  <div className="user-edit-testmode__body">
                    {historyError && <Banner tone="err">Could not load history: {historyError}</Banner>}
                    {!history && !historyError && <Spinner label="Loading history…" />}
                    {history && history.length === 0 && (
                      <p className="cell-dim">
                        No changes recorded yet. Recording started 2026-10-07; earlier grants show only
                        their grant date on the role.
                      </p>
                    )}
                    {history && history.length > 0 && (
                      <table className="data-table">
                        <thead>
                          <tr><th>When</th><th>Who</th><th>What</th><th>Before</th><th>After</th></tr>
                        </thead>
                        <tbody>
                          {history.map((h) => (
                            <tr key={h.auditId}>
                              <td>{new Date(h.changedAt.endsWith('Z') ? h.changedAt : `${h.changedAt}Z`).toLocaleString()}</td>
                              <td><code className="cell-mono">{h.changedBy}</code>{h.source ? <span className="cell-dim"> · {h.source}</span> : null}</td>
                              <td>
                                {auditActionLabel(h.action)}
                                {h.field ? <> — {h.action.startsWith('Role') ? roleLabel(h.field) : h.field}</> : null}
                              </td>
                              <td>{h.oldValue ?? <span className="cell-dim">—</span>}</td>
                              <td>{h.newValue ?? <span className="cell-dim">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </ModalShell>

      {/* Save confirmation — what this Save will do (2026-10-07). */}
      <ModalShell
        open={plan !== null}
        title={`Confirm changes — ${editing?.displayName || editing?.email || ''}`}
        onClose={() => (saving ? undefined : setPlan(null))}
        width={520}
        actions={
          <>
            <Button variant="ghost" onClick={() => setPlan(null)} disabled={saving}>Back</Button>
            <Button variant={plan && (plan.removes.length > 0 || (editing && !editing.isActive)) ? 'danger' : 'primary'}
              onClick={() => plan && void executeSave(plan)} disabled={saving}>
              {saving ? 'Saving…' : 'Confirm and save'}
            </Button>
          </>
        }
      >
        {plan && (
          <ul className="form-bullets">
            {plan.adds.map((c) => <li key={`a-${c}`}>Adds <strong>{roleLabel(c, catalog.find((r) => r.code === c)?.name)}</strong></li>)}
            {plan.removes.map((c) => <li key={`r-${c}`}>Removes <strong>{roleLabel(c, catalog.find((r) => r.code === c)?.name)}</strong></li>)}
            {plan.fieldChanges.map((f) => <li key={`f-${f}`}>{f}</li>)}
            {plan.categoriesChanged && <li>Product categories: {Array.from(enabledCategories).sort().join(', ') || '(none)'}</li>}
          </ul>
        )}
        <p className="form-hint">
          Recorded in Access history under your name. The person sees the change the next time they open the app.
        </p>
      </ModalShell>

      {/* Bulk activate / deactivate confirmation. */}
      <ModalShell
        open={confirmBulk !== null}
        title={confirmBulk ? 'Activate selected users' : 'Deactivate selected users'}
        onClose={() => (bulkBusy ? undefined : setConfirmBulk(null))}
        width={460}
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirmBulk(null)} disabled={bulkBusy}>Cancel</Button>
            <Button variant={confirmBulk ? 'primary' : 'danger'} disabled={bulkBusy}
              onClick={() => { const v = confirmBulk; setConfirmBulk(null); if (v !== null) void bulkSetActive(v); }}>
              {confirmBulk ? 'Activate' : 'Deactivate'} {selectedIds.size}
            </Button>
          </>
        }
      >
        <p>
          {confirmBulk
            ? `Reactivate ${selectedIds.size} account(s)? Their existing roles start working again.`
            : `Deactivate ${selectedIds.size} account(s)? Every role stops working for them (within 5 minutes on the server); their roles and history are kept.`}
        </p>
      </ModalShell>

      {/* Per-user Sync from Entra diff modal */}
      <ModalShell
        open={syncOpen}
        title={`Sync from Entra — ${editing?.displayName ?? ''}`}
        onClose={() => (syncLoading ? undefined : setSyncOpen(false))}
        width={760}
        actions={
          <>
            <Button variant="ghost" onClick={() => setSyncOpen(false)} disabled={syncLoading}>Close</Button>
            {syncDiff && syncDiff.fields.length > 0 && (
              <Button variant="primary"
                disabled={syncLoading || syncAccepted.size === 0}
                onClick={() => void applySync()}>
                {syncLoading ? 'Applying…' : `Apply ${syncAccepted.size}`}
              </Button>
            )}
          </>
        }
      >
        {syncLoading && <Spinner label="Computing diff…" />}
        {syncError && <Banner tone="err">{syncError}</Banner>}
        {!syncLoading && syncDiff && (
          <>
            {!syncDiff.entraResolved ? (
              <Banner tone="warn">Entra returned no user for this OID. Check the user&apos;s EntraObjectId.</Banner>
            ) : syncDiff.fields.length === 0 ? (
              <p>No differences between this user and Entra. (You can still refresh the photo below.)</p>
            ) : (
              <>
                <p className="form-hint">Check the fields you want to apply, then click Apply.</p>
                <table className="data-table">
                  <thead><tr><th><span className="sr-only">Apply</span></th><th>Field</th><th>Current</th><th>Entra</th></tr></thead>
                  <tbody>
                    {syncDiff.fields.map((f) => (
                      <tr key={f.fieldName}>
                        <td>
                          <input type="checkbox"
                            aria-label={`Apply Entra value for ${f.fieldName}`}
                            checked={syncAccepted.has(f.fieldName)}
                            onChange={(e) => {
                              const next = new Set(syncAccepted);
                              if (e.target.checked) next.add(f.fieldName);
                              else next.delete(f.fieldName);
                              setSyncAccepted(next);
                            }} />
                        </td>
                        <td><code className="cell-mono">{f.fieldName}</code></td>
                        <td>{f.current ?? <span className="cell-dim">—</span>}</td>
                        <td>{f.entra ?? <span className="cell-dim">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {syncDiff.ignoredFields.length > 0 && (
              <p className="form-hint">
                Ignored fields (suppressed in Reconcile): {syncDiff.ignoredFields.join(', ')}
              </p>
            )}
            <div className="user-sync-photo">
              <label className="form-bool">
                <input type="checkbox"
                  checked={syncAccepted.has('photo')}
                  onChange={(e) => {
                    const next = new Set(syncAccepted);
                    if (e.target.checked) next.add('photo');
                    else next.delete('photo');
                    setSyncAccepted(next);
                  }} />
                <span>Refresh photo from Entra</span>
              </label>
            </div>
          </>
        )}
      </ModalShell>

      {/* Delete confirm */}
      <ModalShell
        open={confirmDelete !== null}
        title="Delete user"
        onClose={() => (saving ? undefined : setConfirmDelete(null))}
        width={460}
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)} disabled={saving}>Cancel</Button>
            <Button variant="danger" onClick={onDelete} disabled={saving}>
              {saving ? 'Deleting…' : 'Delete'}
            </Button>
          </>
        }
      >
        {saveError && <Banner tone="err">{saveError}</Banner>}
        <p>Permanently delete <strong>{confirmDelete?.email}</strong>? Their role assignments are removed (Global Admin only). Prefer unticking Active — it keeps everything and can be undone. The access history is kept.</p>
      </ModalShell>
    </>
  );
}

function ReadField({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <label className="form-field form-field--read">
      <span className="form-field__label">{label}</span>
      <span className="form-input form-input--read">
        {value ?? <span className="cell-dim">—</span>}
      </span>
    </label>
  );
}
